import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost } from '@/lib/api';

export const INFLOW_SOURCES = ['invoices', 'recurring', 'milestones', 'pipeline'] as const;
export const OUTFLOW_SOURCES = ['expenses', 'bills', 'payroll', 'tax'] as const;
export type FlowSource = (typeof INFLOW_SOURCES)[number] | (typeof OUTFLOW_SOURCES)[number];

export const SOURCE_LABELS: Record<FlowSource, string> = {
  invoices: 'Confirmed invoices',
  recurring: 'Subscriptions & AMC',
  milestones: 'Project milestones',
  pipeline: 'Weighted pipeline',
  expenses: 'Recurring costs',
  bills: 'Vendor bills',
  payroll: 'Payroll estimate',
  tax: 'Tax due',
};

/** Distinct enough to tell apart in a stacked chart, warm side for money in. */
export const SOURCE_COLOURS: Record<FlowSource, string> = {
  invoices: '#16a34a',
  recurring: '#0891b2',
  milestones: '#7c3aed',
  pipeline: '#ca8a04',
  expenses: '#dc2626',
  bills: '#ea580c',
  payroll: '#be123c',
  tax: '#9f1239',
};

export type ForecastScenario = {
  id: string;
  name: string;
  description: string | null;
  kind: string;
  winRatePercent: number;
  churnPercent: number;
  collectionDelayDays: number;
  costInflationPercent: number;
  plannedHiringCost: string | null;
  isDefault: boolean;
};

export type ForecastBucket = {
  key: string;
  label: string;
  granularity: 'week' | 'month';
  start: string;
  end: string;
  opening: string | null;
  inflow: string | null;
  outflow: string | null;
  net: string | null;
  closing: string | null;
  bySource: Record<string, string | null>;
};

export type ForecastLine = {
  bucketKey: string;
  source: FlowSource;
  amount: string | null;
  label: string;
  entityType?: string;
  entityId?: string;
  confidence?: number;
};

export type Forecast = {
  asOf: string;
  scenario: ForecastScenario & { id: string | null };
  openingBalance: string | null;
  closingBalance: string | null;
  series: ForecastBucket[];
  shortfall: { key: string; label: string; closing: string } | null;
  totals: Record<string, string | null>;
  lines: ForecastLine[];
  lineCount: number;
};

export type Budget = {
  id: string;
  periodKey: string;
  kind: 'EXPENSE' | 'REVENUE';
  revenueLine: string | null;
  amount: string | null;
  currency: string;
  note: string | null;
  category: { id: string; name: string } | null;
};

export type VarianceRow = {
  id: string;
  kind: string;
  label: string;
  budgeted: string | null;
  actual: string | null;
  variance: string | null;
  variancePercent: number | null;
  favourable: boolean;
};

export type RecurringExpense = {
  id: string;
  name: string;
  amount: string | null;
  currency: string;
  cycle: string;
  dueDay: number;
  startDate: string;
  endDate: string | null;
  isActive: boolean;
  autoCreateExpense: boolean;
  lastGeneratedFor: string | null;
  category: { id: string; name: string } | null;
  vendor: { id: string; name: string } | null;
};

export function useForecast(params: { scenarioId?: string; sources?: string[]; months?: number }) {
  return useQuery({
    queryKey: ['forecast', params],
    queryFn: () =>
      apiGet<Forecast>('/forecast', {
        params: {
          ...(params.scenarioId ? { scenarioId: params.scenarioId } : {}),
          ...(params.months ? { months: params.months } : {}),
          // Sent as one comma-separated value; the API accepts either shape.
          ...(params.sources?.length ? { sources: params.sources.join(',') } : {}),
        },
      }),
  });
}

export function useScenarios() {
  return useQuery({
    queryKey: ['forecast', 'scenarios'],
    queryFn: () => apiGet<ForecastScenario[]>('/forecast/scenarios'),
  });
}

export function useSaveScenario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<ForecastScenario> & { name: string }) =>
      apiPost<ForecastScenario>('/forecast/scenarios', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['forecast'] }),
  });
}

export function useDeleteScenario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiDelete(`/forecast/scenarios/${id}`);
      return { id };
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['forecast'] }),
  });
}

export function useForecastAccuracy() {
  return useQuery({
    queryKey: ['forecast', 'accuracy'],
    queryFn: () =>
      apiGet<
        {
          id: string;
          periodKey: string;
          takenAt: string;
          scenario: string;
          projectedInflow: string | null;
          actualInflow: string | null;
          projectedOutflow: string | null;
          actualOutflow: string | null;
          variancePercent: number | null;
        }[]
      >('/forecast/accuracy'),
  });
}

export function useRevenueByLine(months = 12) {
  return useQuery({
    queryKey: ['forecast', 'revenue-by-line', months],
    queryFn: () =>
      apiGet<{ lines: string[]; periods: Record<string, string>[] }>('/forecast/revenue-by-line', {
        params: { months },
      }),
  });
}

export function useBudgets(params: { periodKey?: string; kind?: string } = {}) {
  return useQuery({
    queryKey: ['budgets', params],
    queryFn: () => apiGet<Budget[]>('/budgets', { params }),
  });
}

export function useVariance(periodKey: string) {
  return useQuery({
    queryKey: ['budgets', 'variance', periodKey],
    queryFn: () => apiGet<{ periodKey: string; rows: VarianceRow[] }>(`/budgets/variance/${periodKey}`),
    enabled: Boolean(periodKey),
  });
}

export function useSaveBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id?: string;
      periodKey: string;
      kind: 'EXPENSE' | 'REVENUE';
      categoryId?: string | null;
      revenueLine?: string | null;
      amount: string;
      note?: string;
    }) => apiPost<Budget>('/budgets', input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['budgets'] }),
  });
}

export function useDeleteBudget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiDelete(`/budgets/${id}`);
      return { id };
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['budgets'] }),
  });
}

export function useRecurringExpenses() {
  return useQuery({
    queryKey: ['recurring-expenses'],
    queryFn: () => apiGet<RecurringExpense[]>('/recurring-expenses'),
  });
}

export function useSaveRecurringExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id?: string;
      name: string;
      amount: string;
      cycle: string;
      dueDay: number;
      startDate: string;
      endDate?: string | null;
      categoryId?: string | null;
      vendorId?: string | null;
      isActive?: boolean;
      autoCreateExpense?: boolean;
    }) => apiPost<{ id: string; name: string }>('/recurring-expenses', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] });
      void queryClient.invalidateQueries({ queryKey: ['forecast'] });
    },
  });
}

export function useDeleteRecurringExpense() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiDelete(`/recurring-expenses/${id}`);
      return { id };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['recurring-expenses'] });
      void queryClient.invalidateQueries({ queryKey: ['forecast'] });
    },
  });
}
